import { isCompanyAdmin, isWorkspaceAdmin } from '@/features/userInfo/hooks/useUserInfo';
import { useWorkspace } from '@/features/workspaces/hooks';
import { Card, IconButton } from '@/lib';
import { PlusIcon } from '@/lib/Icons/Icons';
import { useGetWorkspaceInvitesQuery } from '@/services/endpoints/workspaces';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NewWorkspaceInviteRow } from './NewWorkspaceInviteRow';
import { WorkspaceInvitesTable } from './WorkspaceInvitesTable';
import { WorkspaceUsersTable, WorkspaceUsersTableSearch } from './WorkspaceUsersTable';
import InformationTip from '@/components/InformationTip';
import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { useGetWorkspaceUsersCountQuery } from '@/services/endpoints/workspaces';

export const WorkspaceUsers: FC = () => {
  const workspace = useWorkspace();
  const { t } = useTranslation('workspaces');
  const { t: tSettings } = useTranslation('settings');

  const [showNewInviteRow, setShowNewInviteRow] = useState<boolean>(false);

  const {
    data: invitesData,
    isFetching: isInvitesFetching,
    isLoading: isInvitesLoading,
  } = useGetWorkspaceInvitesQuery({
    workspaceId: workspace.id,
  });

  const {
    data: currentUsersCount,
    isFetching,
    isLoading,
  } = useGetWorkspaceUsersCountQuery({
    workspaceId: workspace.id,
    searchText: undefined,
  });

  const isAdmin = isCompanyAdmin(workspace.companyId) || isWorkspaceAdmin(workspace.id);

  const isRequiredFieldLoading = isInvitesLoading || isInvitesFetching || isFetching || isLoading;

  // Check if there are any non-completed invites
  const hasActiveInvites = useMemo(() => {
    if (!invitesData?.items) return false;
    return invitesData.items.filter((invite) => invite.status !== 'COMPLETE').length > 0;
  }, [invitesData?.items]);

  const hasMaxLimitReached = useMemo(() => {
    // consider if no key is present: Unlimited users
    if (!workspace?.userLimit) return false;
    if (isRequiredFieldLoading) return true; // TO ensure no action can be taken until we know for sure whether the limit has been reached or not
    return currentUsersCount + (hasActiveInvites ? invitesData?.items.length || 0 : 0) === workspace.userLimit;
  }, [workspace]);

  return (
    <div className='flex flex-col gap-6'>
      <div className='flex flex-col gap-4'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2 mt-4'>
            <h3 className='text-2xl font-semibold text-gray-900'>{tSettings('workspaceUsers.title')}</h3>
            {workspace.userLimit && <InformationTip tooltipContent={tSettings('user_limit_info', { userLimit: workspace.userLimit })} />}
          </div>
          <WorkspaceUsersTableSearch />
        </div>

        <WorkspaceUsersTable workspaceId={workspace.id} companyId={workspace.companyId} hideDelete={!!workspace.userLimit} />
      </div>

      {hasActiveInvites && <WorkspaceInvitesTable workspaceId={workspace.id} companyId={workspace.companyId}></WorkspaceInvitesTable>}

      {isAdmin && !showNewInviteRow && (
        <div className='flex flex-col gap-2 rounded-lg border bg-primary-background p-6 shadow-xs'>
          <div className='flex justify-start items-center gap-2 '>
            <IconButton
              intent='secondary'
              onClick={() => {
                if (!hasMaxLimitReached) {
                  setShowNewInviteRow(true);
                }
              }}
              leadingIcon={<PlusIcon />}
              text={t('users.addNewUser')}
              disabled={hasMaxLimitReached}
            />
            <InformationTip tooltipContent={tSettings('user_limit_info', { userLimit: workspace.userLimit })} />
          </div>
          {!isRequiredFieldLoading && hasMaxLimitReached && (
            <Card className='bg-red-100'>
              <ExclamationCircleIcon className='h-6 w-6 text-red-600 mr-2' />
              <p>{tSettings('invitation_limit_info', { userLimit: workspace.userLimit })}</p>
            </Card>
          )}
        </div>
      )}
      {showNewInviteRow && <NewWorkspaceInviteRow onComplete={() => setShowNewInviteRow(false)} workspace={workspace} />}
    </div>
  );
};
